import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { useSEO } from "../lib/seo";
import {
  Lock,
  RefreshCw,
  Trash2,
  LogOut,
  Inbox,
  Users,
  Clock,
  MousePointerClick,
  FileText,
  Palette,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("hu-HU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatDuration = (sec) => {
  if (!sec || sec < 1) return "< 1 mp";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m} p ${s} mp` : `${s} mp`;
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-[var(--form-card)] p-5 flex items-center gap-4">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[var(--accent)]">
      <Icon size={20} className="text-black" />
    </span>
    <div>
      <p className="text-white heading-font font-bold text-2xl leading-none">{value}</p>
      <p className="text-white/60 text-xs mt-1.5">{label}</p>
    </div>
  </div>
);

const AdminPage = () => {
  useSEO("Admin | New Idea Architect", "", "/admin", true);
  const [password, setPassword] = useState(sessionStorage.getItem("nia_admin_pw") || "");
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [theme, setTheme] = useState("green");
  const [loading, setLoading] = useState(false);
  const [openSession, setOpenSession] = useState(null);

  const authHeaders = (pw) => ({ headers: { "X-Admin-Password": pw } });

  const fetchAll = useCallback(async (pw) => {
    setLoading(true);
    try {
      const [leadsRes, analyticsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/leads`, authHeaders(pw)),
        axios.get(`${API}/analytics/summary`, authHeaders(pw)),
        axios.get(`${API}/settings`),
      ]);
      setLeads(leadsRes.data);
      setAnalytics(analyticsRes.data);
      setTheme(settingsRes.data.theme || "green");
      sessionStorage.setItem("nia_admin_pw", pw);
      setPassword(pw);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Hibás jelszó.");
        sessionStorage.removeItem("nia_admin_pw");
        setPassword("");
      } else {
        toast.error("Nem sikerült betölteni az adatokat.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (password) fetchAll(password);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    fetchAll(input.trim());
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Biztosan törlöd ezt az ajánlatkérést?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, authHeaders(password));
      setLeads((l) => l.filter((x) => x.id !== id));
      toast.success("Ajánlatkérés törölve.");
    } catch {
      toast.error("A törlés nem sikerült.");
    }
  };

  const handleThemeChange = async (newTheme) => {
    try {
      await axios.put(`${API}/settings`, { theme: newTheme }, authHeaders(password));
      setTheme(newTheme);
      document.documentElement.setAttribute("data-theme", newTheme);
      toast.success(
        newTheme === "green"
          ? "Az oldal mostantól zöld-arany színvilágban fut."
          : "Az oldal mostantól fekete-narancs színvilágban fut."
      );
    } catch {
      toast.error("A téma mentése nem sikerült.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("nia_admin_pw");
    setPassword("");
    setLeads([]);
    setAnalytics(null);
    setInput("");
  };

  // Bejelentkező képernyő
  if (!password) {
    return (
      <main className="px-2.5 pb-2.5">
        <div className="rounded-2xl bg-[var(--card-dark)] border border-white/10 min-h-[70vh] flex items-center justify-center p-8">
          <form onSubmit={handleLogin} className="w-full max-w-sm flex flex-col gap-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[var(--accent)]">
                <Lock size={20} className="text-black" />
              </span>
              <div>
                <h1 className="text-white heading-font font-bold text-2xl">Admin belépés</h1>
                <p className="text-white/60 text-sm">Ajánlatkérések és látogatottság</p>
              </div>
            </div>
            <Input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Admin jelszó"
              className="h-12 rounded-lg bg-transparent border-white/40 text-white placeholder:text-white/50 focus-visible:ring-[var(--accent)] focus-visible:border-[var(--accent)]"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-7 py-3.5 bg-[var(--accent)] text-black text-sm font-bold rounded-md hover:bg-white transition-colors duration-200 disabled:opacity-60"
            >
              {loading ? "BELÉPÉS..." : "BELÉPÉS"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  const stats = analytics?.stats;
  const sessions = analytics?.sessions || [];

  return (
    <main className="px-2.5 pb-2.5 flex flex-col gap-2.5">
      <div className="rounded-2xl bg-[var(--card-dark)] border border-white/10 p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-white heading-font font-bold text-3xl sm:text-4xl tracking-tight">Admin</h1>
        <div className="flex gap-2">
          <button
            onClick={() => fetchAll(password)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black text-sm font-bold rounded-md hover:bg-[var(--accent)] transition-colors duration-200 disabled:opacity-60"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Frissítés
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/40 text-white text-sm font-bold rounded-md hover:bg-white hover:text-black transition-colors duration-200"
          >
            <LogOut size={15} /> Kilépés
          </button>
        </div>
      </div>

      <Tabs defaultValue="leads" className="w-full">
        <TabsList className="bg-[var(--card-dark)] border border-white/10 h-auto p-1.5 rounded-xl w-full sm:w-auto flex flex-wrap justify-start">
          <TabsTrigger value="leads" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-black text-white/70 font-bold rounded-lg px-5 py-2.5">
            Ajánlatkérések ({leads.length})
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-black text-white/70 font-bold rounded-lg px-5 py-2.5">
            Látogatottság
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-black text-white/70 font-bold rounded-lg px-5 py-2.5">
            Beállítások
          </TabsTrigger>
        </TabsList>

        {/* ===== AJÁNLATKÉRÉSEK ===== */}
        <TabsContent value="leads" className="mt-2.5">
          <div className="rounded-2xl bg-[var(--card-dark)] border border-white/10 p-4 sm:p-8">
            {leads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-white/50">
                <Inbox size={40} className="mb-4" />
                <p className="font-semibold">Még nincs beérkezett ajánlatkérés.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/15 hover:bg-transparent">
                      <TableHead className="text-white/60 font-bold">Dátum</TableHead>
                      <TableHead className="text-white/60 font-bold">Név</TableHead>
                      <TableHead className="text-white/60 font-bold">Telefon</TableHead>
                      <TableHead className="text-white/60 font-bold">Email</TableHead>
                      <TableHead className="text-white/60 font-bold">Feladat</TableHead>
                      <TableHead className="text-white/60 font-bold">Méret</TableHead>
                      <TableHead className="text-white/60 font-bold text-right">Művelet</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((l) => (
                      <TableRow key={l.id} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-white/70 whitespace-nowrap">{formatDate(l.created_at)}</TableCell>
                        <TableCell className="text-white font-semibold">{l.nev}</TableCell>
                        <TableCell className="text-white/90">
                          <a href={`tel:${l.telefon}`} className="hover:text-[var(--accent)] transition-colors">{l.telefon}</a>
                        </TableCell>
                        <TableCell className="text-white/90">
                          {l.email ? (
                            <a href={`mailto:${l.email}`} className="hover:text-[var(--accent)] transition-colors">{l.email}</a>
                          ) : (
                            <span className="text-white/40">–</span>
                          )}
                        </TableCell>
                        <TableCell className="text-white/90">{l.feladat}</TableCell>
                        <TableCell className="text-white/90">{l.meret}</TableCell>
                        <TableCell className="text-right">
                          <button
                            onClick={() => handleDelete(l.id)}
                            aria-label="Törlés"
                            className="p-2 text-white/50 hover:text-[var(--accent)] transition-colors"
                          >
                            <Trash2 size={17} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== LÁTOGATOTTSÁG ===== */}
        <TabsContent value="analytics" className="mt-2.5 flex flex-col gap-2.5">
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              <StatCard icon={Users} label="Látogató (munkamenet)" value={stats.total_sessions} />
              <StatCard icon={Clock} label="Átlagos idő az oldalon" value={formatDuration(stats.avg_duration_sec)} />
              <StatCard icon={FileText} label="Űrlap kitöltést kezdte" value={stats.form_starts} />
              <StatCard icon={MousePointerClick} label="Ajánlatot küldött" value={stats.form_submits} />
            </div>
          )}

          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
              <div className="rounded-2xl bg-[var(--card-dark)] border border-white/10 p-6">
                <h3 className="text-white font-bold mb-4">Legnézettebb oldalak</h3>
                {stats.top_pages.map(([p, c]) => (
                  <div key={p} className="flex justify-between text-sm py-1.5 border-b border-white/10 last:border-0">
                    <span className="text-white/80 truncate pr-4">{p}</span>
                    <span className="text-[var(--accent)] font-bold">{c}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl bg-[var(--card-dark)] border border-white/10 p-6">
                <h3 className="text-white font-bold mb-1">Kilépési oldalak</h3>
                <p className="text-white/50 text-xs mb-3">Itt hagyták el a látogatók az oldalt („hol akadtak el”)</p>
                {stats.exit_pages.map(([p, c]) => (
                  <div key={p} className="flex justify-between text-sm py-1.5 border-b border-white/10 last:border-0">
                    <span className="text-white/80 truncate pr-4">{p}</span>
                    <span className="text-[var(--accent)] font-bold">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl bg-[var(--card-dark)] border border-white/10 p-4 sm:p-8">
            <h3 className="text-white font-bold mb-4">Munkamenetek (legutóbbi {sessions.length})</h3>
            {sessions.length === 0 ? (
              <p className="text-white/50 py-10 text-center">Még nincs rögzített látogatás.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/15 hover:bg-transparent">
                      <TableHead className="text-white/60 font-bold">Kezdés</TableHead>
                      <TableHead className="text-white/60 font-bold">Forrás</TableHead>
                      <TableHead className="text-white/60 font-bold">Eszköz</TableHead>
                      <TableHead className="text-white/60 font-bold">IP</TableHead>
                      <TableHead className="text-white/60 font-bold">Idő</TableHead>
                      <TableHead className="text-white/60 font-bold">Oldalak</TableHead>
                      <TableHead className="text-white/60 font-bold">Kilépett itt</TableHead>
                      <TableHead className="text-white/60 font-bold text-right">Részletek</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s) => (
                      <Fragment key={s.session_id}>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          <TableCell className="text-white/80 whitespace-nowrap">{formatDate(s.first_ts)}</TableCell>
                          <TableCell className="text-white/90">{s.source}</TableCell>
                          <TableCell className="text-white/90">{s.device}</TableCell>
                          <TableCell className="text-white/60 text-xs">{s.ip}</TableCell>
                          <TableCell className="text-white/90 whitespace-nowrap">{formatDuration(s.duration_sec)}</TableCell>
                          <TableCell className="text-white/90">{s.pages.length}</TableCell>
                          <TableCell className="text-white/80 max-w-[180px] truncate">{s.exit_page}</TableCell>
                          <TableCell className="text-right">
                            <button
                              onClick={() => setOpenSession(openSession === s.session_id ? null : s.session_id)}
                              className="p-2 text-white/60 hover:text-[var(--accent)] transition-colors"
                              aria-label="Részletek"
                            >
                              {openSession === s.session_id ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
                            </button>
                          </TableCell>
                        </TableRow>
                        {openSession === s.session_id && (
                          <TableRow className="border-white/10 bg-white/5 hover:bg-white/5">
                            <TableCell colSpan={8}>
                              <div className="py-2 flex flex-col gap-3">
                                <div>
                                  <p className="text-white/50 text-xs font-bold mb-1.5">BEJÁRT OLDALAK (sorrendben):</p>
                                  <p className="text-white/90 text-sm">{s.pages.join("  →  ") || "–"}</p>
                                </div>
                                <div>
                                  <p className="text-white/50 text-xs font-bold mb-1.5">KATTINTÁSOK:</p>
                                  {s.clicks.length ? (
                                    <div className="flex flex-wrap gap-1.5">
                                      {s.clicks.map((c, i) => (
                                        <span key={i} className="px-2.5 py-1 rounded-md bg-white/10 text-white/85 text-xs">{c}</span>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-white/40 text-sm">Nem kattintott semmire.</p>
                                  )}
                                </div>
                                <div className="flex gap-4 text-sm">
                                  <span className={s.form_start ? "text-[var(--accent)] font-bold" : "text-white/40"}>
                                    {s.form_start ? "✓ Elkezdte az űrlapot" : "✗ Nem kezdte el az űrlapot"}
                                  </span>
                                  <span className={s.form_submit ? "text-[var(--accent)] font-bold" : "text-white/40"}>
                                    {s.form_submit ? "✓ Ajánlatot kért" : "✗ Nem küldte be"}
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== BEÁLLÍTÁSOK ===== */}
        <TabsContent value="settings" className="mt-2.5">
          <div className="rounded-2xl bg-[var(--card-dark)] border border-white/10 p-6 sm:p-10">
            <div className="flex items-center gap-3 mb-6">
              <Palette size={22} className="text-[var(--accent)]" />
              <h3 className="text-white font-bold text-xl heading-font">Színvilág</h3>
            </div>
            <p className="text-white/60 text-sm mb-6 max-w-md">
              Itt állíthatod be, milyen színvilágban fusson az oldal minden látogatónak.
              A módosítás azonnal élesbe lép.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
              <button
                onClick={() => handleThemeChange("green")}
                className={`rounded-xl p-5 text-left border-2 transition-colors duration-200 ${
                  theme === "green" ? "border-[var(--accent)] bg-white/5" : "border-white/15 hover:border-white/40"
                }`}
              >
                <div className="flex gap-2 mb-3">
                  <span className="h-8 w-8 rounded-md" style={{ background: "#0F211A" }} />
                  <span className="h-8 w-8 rounded-md" style={{ background: "#E2A63D" }} />
                  <span className="h-8 w-8 rounded-md" style={{ background: "#F2EDE1" }} />
                </div>
                <p className="text-white font-bold">Zöld–arany</p>
                <p className="text-white/50 text-xs mt-1">Prémium, bizalomépítő megjelenés</p>
                {theme === "green" && <p className="text-[var(--accent)] text-xs font-bold mt-2">✓ AKTÍV</p>}
              </button>
              <button
                onClick={() => handleThemeChange("orange")}
                className={`rounded-xl p-5 text-left border-2 transition-colors duration-200 ${
                  theme === "orange" ? "border-[var(--accent)] bg-white/5" : "border-white/15 hover:border-white/40"
                }`}
              >
                <div className="flex gap-2 mb-3">
                  <span className="h-8 w-8 rounded-md" style={{ background: "#0a0a0a", border: "1px solid #333" }} />
                  <span className="h-8 w-8 rounded-md" style={{ background: "#ff4b26" }} />
                  <span className="h-8 w-8 rounded-md" style={{ background: "#edeae4" }} />
                </div>
                <p className="text-white font-bold">Fekete–narancs</p>
                <p className="text-white/50 text-xs mt-1">Az eredeti, erőteljes megjelenés</p>
                {theme === "orange" && <p className="text-[var(--accent)] text-xs font-bold mt-2">✓ AKTÍV</p>}
              </button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </main>
  );
};

export default AdminPage;
